import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { SiteFooter } from "@/components/SiteFooter";

/**
 * Renders a public policy page (Cancellation, Payment, Privacy, Terms).
 *
 * Props:
 *  - title:        Display title (e.g. "Cancellation Policy")
 *  - eyebrow:      Short uppercase label above the title (e.g. "Policies")
 *  - lastUpdated:  Optional "Last updated" string for the page header
 *  - sections:     Array of { heading?, paragraphs?: string[], bullets?: string[] }
 *  - footnote:     Optional closing paragraph rendered after the sections
 */
export default function PolicyPage({
  title,
  eyebrow = "Policies",
  lastUpdated,
  sections = [],
  footnote,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${title} · T&J Clinical Solutions`;
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
    };
  }, [title]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header
        className="sticky top-0 z-30 border-b bg-white/90 backdrop-blur"
        style={{ borderColor: "#E2E8F0" }}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="policy-back-home-logo" aria-label="T&J Clinical Solutions home">
            <Logo size="md" showText />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium"
            style={{ color: "#0B2545" }}
            data-testid="policy-back-home-link"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back to home</span>
          </Link>
        </div>
      </header>

      <main className="flex-1">
        <section className="mx-auto max-w-3xl px-5 sm:px-8 pt-12 sm:pt-16 pb-6">
          <div
            className="text-[11px] uppercase tracking-[0.22em] font-medium"
            style={{ color: "#D4AF37" }}
            data-testid="policy-eyebrow"
          >
            {eyebrow}
          </div>
          <h1
            className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
            style={{ color: "#0B2545" }}
            data-testid="policy-title"
          >
            {title}
          </h1>
          <div
            className="mt-4 h-[3px] w-14 rounded-full"
            style={{ background: "#D4AF37" }}
          />
          {lastUpdated ? (
            <p
              className="mt-5 text-xs font-medium"
              style={{ color: "#475569" }}
              data-testid="policy-last-updated"
            >
              Last updated: {lastUpdated}
            </p>
          ) : null}
        </section>

        <section className="mx-auto max-w-3xl px-5 sm:px-8 pb-20">
          <div
            className="rounded-2xl border bg-white p-6 sm:p-10 shadow-[0_1px_2px_rgba(11,37,69,0.04)]"
            style={{ borderColor: "#E2E8F0" }}
            data-testid="policy-body"
          >
            <div className="space-y-7 sm:space-y-8">
              {sections.map((section, i) => (
                <PolicySection key={i} {...section} />
              ))}
              {footnote ? (
                <p
                  className="text-sm leading-relaxed pt-2"
                  style={{ color: "#0F172A" }}
                  data-testid="policy-footnote"
                >
                  {footnote}
                </p>
              ) : null}
            </div>

            <div
              className="mt-10 pt-6 border-t text-xs"
              style={{ borderColor: "#E2E8F0", color: "#475569" }}
            >
              Questions about this policy? Reach our team at{" "}
              <a
                href="mailto:tj.clinicalsolutions@gmail.com"
                className="font-medium underline"
                style={{ color: "#0B2545" }}
              >
                tj.clinicalsolutions@gmail.com
              </a>
              .
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

const PolicySection = ({ heading, paragraphs = [], bullets = [] }) => (
  <div>
    {heading ? (
      <h2
        className="font-display text-lg sm:text-xl font-bold tracking-tight"
        style={{ color: "#0B2545" }}
      >
        {heading}
      </h2>
    ) : null}
    {paragraphs.map((p, i) => (
      <p
        key={i}
        className={`text-sm sm:text-base leading-relaxed ${
          heading || i > 0 ? "mt-3" : ""
        }`}
        style={{ color: "#0F172A" }}
      >
        {p}
      </p>
    ))}
    {bullets.length > 0 ? (
      <ul
        className="mt-3 space-y-1.5 text-sm sm:text-base leading-relaxed pl-5"
        style={{ color: "#0F172A", listStyle: "disc" }}
      >
        {bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    ) : null}
  </div>
);
